import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Edit2,
  Expand,
  ExternalLink,
  Eye,
  Heart,
  Info,
  Loader2,
  Play,
  Plus,
  X,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEffect, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Id } from "~/convex/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { VideoEmbed } from "@/components/ui/video-embed";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";

interface CreatorProfile {
  _id: Id<"exp__creatorProfile">;
  handle: string;
  platform: string;
  name: string;
  bio: string;
  followers: number;
  videos: number;
  image?: string | Id<"_storage">;
  contentNiche?: string[];
  topContentTopics?: string[];
  contentStyle?: string;
  audienceAnalysis?: string;
}

interface TopVideo {
  _id: string;
  title?: string;
  views?: number;
  likes?: number;
  commentCount: number;
  videoUrl?: string;
  platform?: string;
}

interface ProfileAnalysisProps {
  creatorProfile: CreatorProfile | null;
  topVideos?: TopVideo[];
  isAnalyzing?: boolean;
  onAnalyzeProfile?: () => void;
  selectedAccountHandle?: string;
}

const AnalysisLoadingScreen = () => {
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);

  const loadingMessages = [
    "Watching your videos 👀",
    "Analyzing your content style 🎨",
    "Understanding your audience 🎯",
    "Identifying trending topics 📈",
    "Mapping content categories 🗂️",
    "Studying engagement patterns 💬",
    "Discovering your unique voice 🎤",
    "Reading the comments section 🔍",
    "Processing video metadata 📊",
    "Generating insights ✨",
  ];

  useEffect(() => {
    // Progress bar animation - complete in 2 minutes (120 seconds)
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const increment = 100 / (120 * 4); // Update 4 times per second for smooth animation
        const newProgress = prev + increment;
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, 250);

    // Message rotation - change every 8 seconds
    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 8000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
    };
  }, []);

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="text-center py-8 max-w-md mx-auto">
        {/* Animated icon */}
        <div className="relative mb-6">
          <Loader2 className="w-12 h-12 mx-auto animate-spin text-blue-500" />
          <div className="absolute inset-0 w-12 h-12 mx-auto rounded-full border-2 border-blue-200 dark:border-blue-800 animate-pulse" />
        </div>

        {/* Main title */}
        <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-gray-100">
          Generating Insights
        </h3>

        {/* Dynamic message */}
        <div className="h-6 mb-6">
          <p className="text-base text-blue-600 dark:text-blue-400 font-medium transition-all duration-500 ease-in-out">
            {loadingMessages[messageIndex]}
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-2.5 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            {Math.round(progress)}% complete
          </p>
        </div>

        {/* Time estimate */}
        <p className="text-xs text-gray-500 dark:text-gray-500">
          This analysis takes 2-5 minutes. The page will update automatically
          when complete.
        </p>

        {/* Subtle animation elements */}
        <div className="flex justify-center space-x-1 mt-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"
              style={{
                animationDelay: `${i * 0.2}s`,
                animationDuration: "1.5s",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const formatNumber = (num: number) => {
  return new Intl.NumberFormat("en-US", { notation: "compact" }).format(num);
};

// Helper component to handle creator profile images (both URLs and storage IDs)
const CreatorProfileImage = ({
  image,
  name,
  className,
}: {
  image?: string | Id<"_storage">;
  name: string;
  className: string;
}) => {
  // Check if image is a storage ID or URL
  const isStorageId =
    image &&
    typeof image === "string" &&
    !image.startsWith("http") &&
    !image.startsWith("data:");

  const { data: imageUrl } = useQuery({
    ...convexQuery(api.exp__creatorInsights.exp__getCreatorProfileImageUrl, {
      storageId: image as Id<"_storage">,
    }),
    enabled: !!isStorageId,
  });

  const displayUrl = isStorageId
    ? imageUrl
    : typeof image === "string"
      ? image
      : undefined;

  if (!displayUrl) {
    return (
      <div className={className}>
        <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/50 dark:to-purple-900/50 flex items-center justify-center">
          <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">
            {name.charAt(0).toUpperCase()}
          </span>
        </div>
      </div>
    );
  }

  return <img src={displayUrl} alt={name} className={className} />;
};

export const ProfileAnalysis = ({
  creatorProfile,
  topVideos,
  isAnalyzing = false,
  onAnalyzeProfile,
  selectedAccountHandle,
}: ProfileAnalysisProps) => {
  const { toast } = useToast();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<string | null>(null);

  // Edit state
  const [isEditingNiche, setIsEditingNiche] = useState(false);
  const [isEditingTopics, setIsEditingTopics] = useState(false);
  const [editedNiche, setEditedNiche] = useState<string[]>([]);
  const [editedTopics, setEditedTopics] = useState<string[]>([]);
  const [newNicheInput, setNewNicheInput] = useState("");
  const [newTopicInput, setNewTopicInput] = useState("");

  // Mutations for updating profile
  const updateContentNiche = useMutation(
    api.exp__creatorInsights.exp__updateCreatorContentNiche,
  );
  const updateTopTopics = useMutation(
    api.exp__creatorInsights.exp__updateCreatorTopTopics,
  );

  // Draw connections on canvas
  const drawConnections = () => {
    const canvas = canvasRef.current;
    if (!canvas || !creatorProfile) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size to match its display size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    // Clear canvas
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Set up gradient background
    const gradient = ctx.createRadialGradient(
      rect.width / 2,
      rect.height / 2,
      0,
      rect.width / 2,
      rect.height / 2,
      Math.min(rect.width, rect.height) / 2,
    );
    gradient.addColorStop(0, "rgba(59, 130, 246, 0.05)");
    gradient.addColorStop(1, "rgba(59, 130, 246, 0.01)");

    // Fill background with gradient
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, rect.width, rect.height);

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // Define connection points for each section with proper angles
    const sections = [
      { angle: Math.PI / 4, name: "niche", color: "59, 130, 246" }, // Top right (blue)
      { angle: (3 * Math.PI) / 4, name: "topics", color: "147, 51, 234" }, // Top left (purple)
      { angle: 0, name: "videos", color: "236, 72, 153" }, // Right (pink)
      { angle: Math.PI, name: "stats", color: "99, 102, 241" }, // Left (indigo)
      { angle: (5 * Math.PI) / 4, name: "style", color: "34, 197, 94" }, // Bottom left (green)
      { angle: (7 * Math.PI) / 4, name: "audience", color: "249, 115, 22" }, // Bottom right (orange)
    ];

    // Draw connections
    sections.forEach((section) => {
      const radius = Math.min(rect.width, rect.height) * 0.32;
      const endX = centerX + Math.cos(section.angle) * radius;
      const endY = centerY + Math.sin(section.angle) * radius;

      const isHovered = hoveredSection === section.name;

      // Draw connection line with gradient
      const lineGradient = ctx.createLinearGradient(
        centerX,
        centerY,
        endX,
        endY,
      );
      lineGradient.addColorStop(0, `rgba(${section.color}, 0.3)`);
      lineGradient.addColorStop(
        1,
        `rgba(${section.color}, ${isHovered ? 0.8 : 0.5})`,
      );

      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(endX, endY);
      ctx.strokeStyle = lineGradient;
      ctx.lineWidth = isHovered ? 3 : 2;
      ctx.lineCap = "round";
      ctx.stroke();

      // Draw arrow head
      const arrowLength = 12;
      const arrowAngle = Math.PI / 6;

      ctx.beginPath();
      ctx.moveTo(endX, endY);
      ctx.lineTo(
        endX - arrowLength * Math.cos(section.angle - arrowAngle),
        endY - arrowLength * Math.sin(section.angle - arrowAngle),
      );
      ctx.moveTo(endX, endY);
      ctx.lineTo(
        endX - arrowLength * Math.cos(section.angle + arrowAngle),
        endY - arrowLength * Math.sin(section.angle + arrowAngle),
      );
      ctx.strokeStyle = `rgba(${section.color}, ${isHovered ? 0.9 : 0.7})`;
      ctx.lineWidth = isHovered ? 2.5 : 2;
      ctx.stroke();

      // Draw glowing node at end
      if (isHovered) {
        ctx.beginPath();
        ctx.arc(endX, endY, 12, 0, 2 * Math.PI);
        ctx.fillStyle = `rgba(${section.color}, 0.2)`;
        ctx.fill();
      }

      ctx.beginPath();
      ctx.arc(endX, endY, isHovered ? 8 : 6, 0, 2 * Math.PI);
      ctx.fillStyle = `rgba(${section.color}, ${isHovered ? 1 : 0.8})`;
      ctx.fill();
      ctx.strokeStyle = `rgba(255, 255, 255, 0.8)`;
      ctx.lineWidth = 1;
      ctx.stroke();
    });

    // Draw center circle with enhanced styling
    ctx.beginPath();
    ctx.arc(centerX, centerY, 45, 0, 2 * Math.PI);
    ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
    ctx.fill();
    ctx.strokeStyle = "rgba(59, 130, 246, 0.3)";
    ctx.lineWidth = 3;
    ctx.stroke();

    // Inner glow for center
    ctx.beginPath();
    ctx.arc(centerX, centerY, 35, 0, 2 * Math.PI);
    ctx.strokeStyle = "rgba(59, 130, 246, 0.1)";
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  // Update canvas size on window resize
  useEffect(() => {
    const updateSize = () => {
      const container = canvasRef.current?.parentElement;
      if (container) {
        const rect = container.getBoundingClientRect();
        setCanvasSize({
          width: rect.width || 800,
          height: rect.height || 600,
        });
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // Redraw canvas when dependencies change
  useEffect(() => {
    drawConnections();
  }, [creatorProfile, hoveredSection, canvasSize]);

  // Initialize edit states when profile changes
  useEffect(() => {
    if (creatorProfile) {
      setEditedNiche(creatorProfile.contentNiche || []);
      setEditedTopics(creatorProfile.topContentTopics || []);
    }
  }, [creatorProfile]);

  // Helper functions for editing
  const handleStartEditNiche = () => {
    setIsEditingNiche(true);
    setEditedNiche([...(creatorProfile?.contentNiche || [])]);
    setNewNicheInput("");
  };

  const handleStartEditTopics = () => {
    setIsEditingTopics(true);
    setEditedTopics([...(creatorProfile?.topContentTopics || [])]);
    setNewTopicInput("");
  };

  const handleAddNiche = async () => {
    if (newNicheInput.trim() && !editedNiche.includes(newNicheInput.trim())) {
      const newNiche = [...editedNiche, newNicheInput.trim()];
      setEditedNiche(newNiche);
      setNewNicheInput("");

      // Auto-save
      if (creatorProfile) {
        try {
          await updateContentNiche({
            creatorId: creatorProfile._id,
            contentNiche: newNiche,
          });

          toast({
            title: "Content Niche Updated!",
            description: `Added "${newNicheInput.trim()}" to your content niche.`,
          });
        } catch (error) {
          // Revert on error
          setEditedNiche(editedNiche);
          toast({
            title: "Update Failed",
            description: "Failed to add niche. Please try again.",
            variant: "destructive",
          });
        }
      }
    }
  };

  const handleAddTopic = async () => {
    if (newTopicInput.trim() && !editedTopics.includes(newTopicInput.trim())) {
      const newTopics = [...editedTopics, newTopicInput.trim()];
      setEditedTopics(newTopics);
      setNewTopicInput("");

      // Auto-save
      if (creatorProfile) {
        try {
          await updateTopTopics({
            creatorId: creatorProfile._id,
            topContentTopics: newTopics,
          });

          toast({
            title: "Top Topics Updated!",
            description: `Added "${newTopicInput.trim()}" to your top topics.`,
          });
        } catch (error) {
          // Revert on error
          setEditedTopics(editedTopics);
          toast({
            title: "Update Failed",
            description: "Failed to add topic. Please try again.",
            variant: "destructive",
          });
        }
      }
    }
  };

  const handleRemoveNiche = async (niche: string) => {
    const newNiche = editedNiche.filter((n) => n !== niche);
    setEditedNiche(newNiche);

    // Auto-save
    if (creatorProfile) {
      try {
        await updateContentNiche({
          creatorId: creatorProfile._id,
          contentNiche: newNiche,
        });

        toast({
          title: "Content Niche Updated!",
          description: `Removed "${niche}" from your content niche.`,
        });
      } catch (error) {
        // Revert on error
        setEditedNiche(editedNiche);
        toast({
          title: "Update Failed",
          description: "Failed to remove niche. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleRemoveTopic = async (topic: string) => {
    const newTopics = editedTopics.filter((t) => t !== topic);
    setEditedTopics(newTopics);

    // Auto-save
    if (creatorProfile) {
      try {
        await updateTopTopics({
          creatorId: creatorProfile._id,
          topContentTopics: newTopics,
        });

        toast({
          title: "Top Topics Updated!",
          description: `Removed "${topic}" from your top topics.`,
        });
      } catch (error) {
        // Revert on error
        setEditedTopics(editedTopics);
        toast({
          title: "Update Failed",
          description: "Failed to remove topic. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Analysis */}
      <Card className="overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                Creator Mind Map
              </CardTitle>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Insights for @{selectedAccountHandle}
              </p>
            </div>
            {!creatorProfile && !isAnalyzing && onAnalyzeProfile && (
              <Button
                onClick={onAnalyzeProfile}
                disabled={isAnalyzing}
                variant="outline"
                className="hover:bg-blue-50 hover:border-blue-300"
              >
                Analyze Profile
              </Button>
            )}
            {isAnalyzing && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyzing profile...
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {creatorProfile ? (
            <>
              {/* Spider Chart Container */}
              <div className="relative min-h-[600px] bg-gradient-to-br from-blue-50/30 to-purple-50/30 dark:from-blue-950/30 dark:to-purple-950/30">
                {/* Only show canvas and center avatar when analysis is complete */}
                {creatorProfile.contentNiche &&
                  creatorProfile.topContentTopics &&
                  creatorProfile.contentStyle &&
                  creatorProfile.audienceAnalysis &&
                  !isAnalyzing && (
                    <>
                      {/* Canvas for connections */}
                      <canvas
                        ref={canvasRef}
                        className="absolute inset-0 pointer-events-none w-full h-full"
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "block",
                        }}
                      />

                      {/* Center Avatar */}
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                        <div className="relative">
                          <div className="w-24 h-24 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 p-1 shadow-lg">
                            <div className="w-full h-full rounded-full bg-white dark:bg-gray-900 flex items-center justify-center overflow-hidden">
                              <CreatorProfileImage
                                image={creatorProfile.image}
                                name={creatorProfile.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                          <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 text-center">
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              @{creatorProfile.handle}
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                {/* AI Analysis - Only show when complete */}
                {creatorProfile.contentNiche &&
                creatorProfile.topContentTopics &&
                creatorProfile.contentStyle &&
                creatorProfile.audienceAnalysis &&
                !isAnalyzing ? (
                  <>
                    {/* Audience Analysis - Bottom Right */}
                    <Dialog
                      open={openDialog === "audience"}
                      onOpenChange={(open) =>
                        setOpenDialog(open ? "audience" : null)
                      }
                    >
                      <DialogTrigger asChild>
                        <div
                          className="absolute top-16 right-8 w-72 transform transition-all duration-300 hover:scale-105 cursor-pointer"
                          onMouseEnter={() => setHoveredSection("audience")}
                          onMouseLeave={() => setHoveredSection(null)}
                        >
                          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-orange-200/50 dark:border-orange-700/50 hover:border-orange-400/70 transition-colors">
                            <h3 className="font-semibold text-sm mb-2 text-orange-900 dark:text-orange-100 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                                Audience Insights
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="w-3 h-3 text-orange-500 opacity-60 hover:opacity-100 cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs">
                                        We collected this info based on the
                                        comment section of your latest videos.
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <Expand className="w-3 h-3 opacity-60" />
                            </h3>
                            <div className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                              {creatorProfile.audienceAnalysis
                                ?.split("\n• ")
                                .slice(0, 3)
                                .map((point, index) => {
                                  const cleanPoint = point.trim();
                                  if (!cleanPoint) return null;
                                  return (
                                    <div
                                      key={index}
                                      className="flex items-start gap-1"
                                    >
                                      <span className="text-orange-500 dark:text-orange-400 mt-0.5 flex-shrink-0">
                                        •
                                      </span>
                                      <span className="truncate">
                                        {cleanPoint.length > 40
                                          ? cleanPoint.substring(0, 40) + "..."
                                          : cleanPoint}
                                      </span>
                                    </div>
                                  );
                                })
                                .filter(Boolean)}
                            </div>
                          </div>
                        </div>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                            Audience Analysis
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-3">
                            {creatorProfile.audienceAnalysis
                              ?.split("\n• ")
                              .map((point, index) => {
                                const cleanPoint = point.trim();
                                if (!cleanPoint) return null;
                                return (
                                  <div
                                    key={index}
                                    className="flex items-start gap-3 p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg"
                                  >
                                    <span className="text-orange-500 dark:text-orange-400 mt-1 flex-shrink-0">
                                      •
                                    </span>
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                      {cleanPoint}
                                    </span>
                                  </div>
                                );
                              })
                              .filter(Boolean)}
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* Top Content Topics - Top Left */}
                    <Dialog
                      open={openDialog === "topics"}
                      onOpenChange={(open) => {
                        setOpenDialog(open ? "topics" : null);
                        if (!open) {
                          setIsEditingTopics(false);
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <div
                          className="absolute bottom-16 left-8 w-64 transform transition-all duration-300 hover:scale-105 cursor-pointer"
                          onMouseEnter={() => setHoveredSection("topics")}
                          onMouseLeave={() => setHoveredSection(null)}
                        >
                          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-purple-200/50 dark:border-purple-700/50 hover:border-purple-400/70 transition-colors">
                            <h3 className="font-semibold text-sm mb-2 text-purple-900 dark:text-purple-100 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                                Top Topics
                                <Edit2 className="w-3 h-3 text-purple-400 hover:text-purple-600 transition-colors" />
                              </div>
                              <Expand className="w-3 h-3 opacity-60" />
                            </h3>
                            <div className="flex flex-wrap gap-1">
                              {creatorProfile.topContentTopics
                                .slice(0, 4)
                                .map((topic: string, index: number) => (
                                  <Badge
                                    key={index}
                                    variant="outline"
                                    className="text-xs"
                                  >
                                    {topic}
                                  </Badge>
                                ))}
                            </div>
                          </div>
                        </div>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                              Top Content Topics
                            </div>
                            {!isEditingTopics && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleStartEditTopics}
                                className="flex items-center gap-1 mr-4"
                              >
                                <Edit2 className="w-3 h-3" />
                                Edit
                              </Button>
                            )}
                          </DialogTitle>
                          <DialogDescription>
                            These are the most frequently discussed subjects in
                            your content, helping to attract viewers interested
                            in these specific topics.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          {isEditingTopics ? (
                            <>
                              <div className="space-y-3">
                                <div className="flex flex-wrap gap-2">
                                  {editedTopics.map(
                                    (topic: string, index: number) => (
                                      <div
                                        key={index}
                                        className="flex items-center gap-1"
                                      >
                                        <Badge
                                          variant="outline"
                                          className="text-sm pr-1"
                                        >
                                          {topic}
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                              handleRemoveTopic(topic)
                                            }
                                            className="h-4 w-4 p-0 ml-1 hover:bg-destructive hover:text-destructive-foreground"
                                          >
                                            <X className="w-3 h-3" />
                                          </Button>
                                        </Badge>
                                      </div>
                                    ),
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <Input
                                    placeholder="Add new topic..."
                                    value={newTopicInput}
                                    onChange={(e) =>
                                      setNewTopicInput(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        handleAddTopic();
                                      }
                                    }}
                                    className="flex-1"
                                  />
                                  <Button
                                    onClick={handleAddTopic}
                                    disabled={!newTopicInput.trim()}
                                    size="sm"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {creatorProfile.topContentTopics?.map(
                                (topic: string, index: number) => (
                                  <Badge
                                    key={index}
                                    variant="outline"
                                    className="text-sm"
                                  >
                                    {topic}
                                  </Badge>
                                ),
                              )}
                            </div>
                          )}
                        </div>
                        {isEditingTopics && (
                          <DialogFooter>
                            <Button onClick={() => setIsEditingTopics(false)}>
                              Done
                            </Button>
                          </DialogFooter>
                        )}
                      </DialogContent>
                    </Dialog>

                    {/* Content Style - Bottom Left */}
                    <Dialog
                      open={openDialog === "style"}
                      onOpenChange={(open) =>
                        setOpenDialog(open ? "style" : null)
                      }
                    >
                      <DialogTrigger asChild>
                        <div
                          className="absolute top-16 left-8 w-72 transform transition-all duration-300 hover:scale-105 cursor-pointer"
                          onMouseEnter={() => setHoveredSection("style")}
                          onMouseLeave={() => setHoveredSection(null)}
                        >
                          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-green-200/50 dark:border-green-700/50 hover:border-green-400/70 transition-colors">
                            <h3 className="font-semibold text-sm mb-2 text-green-900 dark:text-green-100 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                Content Style
                              </div>
                              <Expand className="w-3 h-3 opacity-60" />
                            </h3>
                            <div className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                              {creatorProfile.contentStyle
                                ?.split("\n• ")
                                .slice(0, 3)
                                .map((point, index) => {
                                  const cleanPoint = point.trim();
                                  if (!cleanPoint) return null;
                                  return (
                                    <div
                                      key={index}
                                      className="flex items-start gap-1"
                                    >
                                      <span className="text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0">
                                        •
                                      </span>
                                      <span className="truncate">
                                        {cleanPoint.length > 40
                                          ? cleanPoint.substring(0, 40) + "..."
                                          : cleanPoint}
                                      </span>
                                    </div>
                                  );
                                })
                                .filter(Boolean)}
                            </div>
                          </div>
                        </div>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            Content Style Analysis
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-3">
                            {creatorProfile.contentStyle
                              ?.split("\n• ")
                              .map((point, index) => {
                                const cleanPoint = point.trim();
                                if (!cleanPoint) return null;
                                return (
                                  <div
                                    key={index}
                                    className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg"
                                  >
                                    <span className="text-green-500 dark:text-green-400 mt-1 flex-shrink-0">
                                      •
                                    </span>
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                      {cleanPoint}
                                    </span>
                                  </div>
                                );
                              })
                              .filter(Boolean)}
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* Account Stats - Left */}
                    <div
                      className="absolute top-1/2 left-8 transform -translate-y-1/2 w-48 transition-all duration-300 hover:scale-105"
                      onMouseEnter={() => setHoveredSection("stats")}
                      onMouseLeave={() => setHoveredSection(null)}
                    >
                      <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-indigo-200/50 dark:border-indigo-700/50">
                        <h3 className="font-semibold text-sm mb-3 text-indigo-900 dark:text-indigo-100 flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                          Account Stats
                        </h3>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              Followers
                            </span>
                            <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">
                              {formatNumber(creatorProfile.followers)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              Videos
                            </span>
                            <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">
                              {creatorProfile.videos}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Top Recent Videos Preview - Right */}
                    {topVideos && topVideos.length > 0 && (
                      <Dialog
                        open={openDialog === "videos"}
                        onOpenChange={(open) =>
                          setOpenDialog(open ? "videos" : null)
                        }
                      >
                        <DialogTrigger asChild>
                          <div
                            className="absolute top-1/2 right-8 transform -translate-y-1/2 w-56 transition-all duration-300 hover:scale-105 cursor-pointer"
                            onMouseEnter={() => setHoveredSection("videos")}
                            onMouseLeave={() => setHoveredSection(null)}
                          >
                            <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-pink-200/50 dark:border-pink-700/50 hover:border-pink-400/70 transition-colors">
                              <h3 className="font-semibold text-sm mb-3 text-pink-900 dark:text-pink-100 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full bg-pink-500"></div>
                                  Recent Videos
                                </div>
                                <Expand className="w-3 h-3 opacity-60" />
                              </h3>
                              <div className="space-y-2">
                                {topVideos.slice(0, 2).map((video) => (
                                  <div key={video._id} className="text-xs">
                                    <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                      {video.title || "Untitled Video"}
                                    </p>
                                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mt-1">
                                      {video.views && (
                                        <div className="flex items-center gap-1">
                                          <Eye className="w-3 h-3" />
                                          <span>
                                            {formatNumber(video.views)}
                                          </span>
                                        </div>
                                      )}
                                      {video.likes && (
                                        <div className="flex items-center gap-1">
                                          <Heart className="w-3 h-3" />
                                          <span>
                                            {formatNumber(video.likes)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl">
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-pink-500"></div>
                              Recent Videos
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 max-h-96 overflow-y-auto">
                            {topVideos.map((video, index) => (
                              <div
                                key={video._id}
                                className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                              >
                                <div className="flex-shrink-0">
                                  {video.videoUrl ? (
                                    <div className="aspect-video w-48 h-96">
                                      <VideoEmbed
                                        url={video.videoUrl}
                                        platform={
                                          video.platform ||
                                          creatorProfile?.platform
                                        }
                                        className="w-full h-full object-contain"
                                      />
                                    </div>
                                  ) : (
                                    <div className="w-32 h-20 bg-gradient-to-br from-pink-100 to-purple-100 dark:from-pink-900/50 dark:to-purple-900/50 rounded flex items-center justify-center">
                                      <Play className="w-6 h-6 text-pink-600 dark:text-pink-400" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                      {video.title || "Untitled Video"}
                                    </h4>
                                    <span className="text-xs text-pink-600 dark:text-pink-400 font-medium">
                                      #{index + 1}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    {video.views && (
                                      <div className="flex items-center gap-1">
                                        <Eye className="w-3 h-3" />
                                        <span>{formatNumber(video.views)}</span>
                                      </div>
                                    )}
                                    {video.likes && (
                                      <div className="flex items-center gap-1">
                                        <Heart className="w-3 h-3" />
                                        <span>{formatNumber(video.likes)}</span>
                                      </div>
                                    )}
                                    <span>{video.commentCount} comments</span>
                                  </div>
                                  {video.videoUrl && (
                                    <a
                                      href={video.videoUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 mt-2 text-xs text-pink-600 dark:text-pink-400 hover:underline"
                                    >
                                      Watch Video
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}

                    {/* Content Niche - Top Right */}
                    <Dialog
                      open={openDialog === "niche"}
                      onOpenChange={(open) => {
                        setOpenDialog(open ? "niche" : null);
                        if (!open) {
                          setIsEditingNiche(false);
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <div
                          className="absolute bottom-16 right-8 w-64 transform transition-all duration-300 hover:scale-105 cursor-pointer"
                          onMouseEnter={() => setHoveredSection("niche")}
                          onMouseLeave={() => setHoveredSection(null)}
                        >
                          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-blue-200/50 dark:border-blue-700/50 hover:border-blue-400/70 transition-colors">
                            <h3 className="font-semibold text-sm mb-2 text-blue-900 dark:text-blue-100 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                Content Niche
                                <Edit2 className="w-3 h-3 text-blue-400 hover:text-blue-600 transition-colors" />
                              </div>
                              <Expand className="w-3 h-3 opacity-60" />
                            </h3>
                            <div className="flex flex-wrap gap-1">
                              {creatorProfile.contentNiche
                                .slice(0, 4)
                                .map((niche: string, index: number) => (
                                  <Badge
                                    key={index}
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    {niche}
                                  </Badge>
                                ))}
                            </div>
                          </div>
                        </div>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                              Content Niche Analysis
                            </div>
                            {!isEditingNiche && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleStartEditNiche}
                                className="flex items-center gap-1 mr-4"
                              >
                                <Edit2 className="w-3 h-3" />
                                Edit
                              </Button>
                            )}
                          </DialogTitle>
                          <DialogDescription>
                            These are the primary content categories that define
                            your channel's focus and help audience discovery.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          {isEditingNiche ? (
                            <>
                              <div className="space-y-3">
                                <div className="flex flex-wrap gap-2">
                                  {editedNiche.map(
                                    (niche: string, index: number) => (
                                      <div
                                        key={index}
                                        className="flex items-center gap-1"
                                      >
                                        <Badge
                                          variant="secondary"
                                          className="text-sm pr-1"
                                        >
                                          {niche}
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                              handleRemoveNiche(niche)
                                            }
                                            className="h-4 w-4 p-0 ml-1 hover:bg-destructive hover:text-destructive-foreground"
                                          >
                                            <X className="w-3 h-3" />
                                          </Button>
                                        </Badge>
                                      </div>
                                    ),
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <Input
                                    placeholder="Add new niche..."
                                    value={newNicheInput}
                                    onChange={(e) =>
                                      setNewNicheInput(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        handleAddNiche();
                                      }
                                    }}
                                    className="flex-1"
                                  />
                                  <Button
                                    onClick={handleAddNiche}
                                    disabled={!newNicheInput.trim()}
                                    size="sm"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {creatorProfile.contentNiche?.map(
                                (niche: string, index: number) => (
                                  <Badge
                                    key={index}
                                    variant="secondary"
                                    className="text-sm"
                                  >
                                    {niche}
                                  </Badge>
                                ),
                              )}
                            </div>
                          )}
                        </div>
                        {isEditingNiche && (
                          <DialogFooter>
                            <Button onClick={() => setIsEditingNiche(false)}>
                              Done
                            </Button>
                          </DialogFooter>
                        )}
                      </DialogContent>
                    </Dialog>
                  </>
                ) : (
                  <AnalysisLoadingScreen />
                )}

                {/* Show loading screen when analyzing */}
                {isAnalyzing && <AnalysisLoadingScreen />}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">@</span>
                </div>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Click "Analyze Profile" to generate your personalized creator
                  mind map
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Our platform will create a visual network of your content
                  insights
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileAnalysis;
